import { ContentState, DraftHandleValue, EditorState } from "draft-js";
import { metadata } from "~/EasyProjectsCommon/EntitiesMetadata";
import { Team, User } from "~/normalizr/entities";
import { MessageFormatter } from "~/Redux/AvailableUsers/mentions/MessageFormatter";
import { appendMentions } from "~/sharedComponents/advancedEditor/utils/appendMentions";
import { setEditorStateAfterInsert } from "~/sharedComponents/advancedEditor/utils/setEditorStateAfterInsert";
import { normalizeMentions } from "./utils/normalizeMentions";

export const getText = (editorState: EditorState) => normalizeMentions(editorState);
export const getInitialState = (prevState?: EditorState) => {
    if (!prevState) {
        return EditorState.createEmpty();
    }

    const content = ContentState.createFromText("");
    const state = EditorState.push(prevState, content, "remove-range");

    // forceSelection used for cleanup after post;
    // if not to do this and start typing letter again -> caret suddenly moves to start
    // (editor triggered new onChange event with broken caret position, not sure why)
    return EditorState.forceSelection(state, content.getSelectionAfter());
};

export const getInitialStateFromText = (text: string = "", users: User[], teams: Team[]) => {
    let content = ContentState.createFromText(text);

    if (text && users?.length) {
        const messageFormatter = new MessageFormatter(users, teams);
        content = appendMentionEntities(content, messageFormatter);
    }
    return EditorState.createWithContent(content);
};

export const handleBeforeInput = (chars: string, editorState: EditorState): DraftHandleValue => {
    const text = getText(editorState);

    if (text.length - getSelectionLength(editorState) >= metadata.Message.MessageTextMaxLength) {
        return "handled";
    }

    return "not-handled";
};

const getSelectionLength = (editorState: EditorState): number => {
    const selection = editorState.getSelection();

    const acc = editorState
        .getCurrentContent()
        .getBlockMap()
        .toSeq()
        .skipUntil(b => b.getKey() === selection.getStartKey())
        .takeUntil(b => b.getKey() === selection.getEndKey())
        .reduce((acc, block) => acc + block.getLength(), 0);

    return acc + selection.getEndOffset() - selection.getStartOffset();
};

export const internal = {
    getSelectionLength,
};

export const insertMentions = (state: EditorState, users: User[], teams: Team[]) => {
    const messageFormatter = new MessageFormatter(users, teams);
    const initContent = state.getCurrentContent();

    let content = appendMentionEntities(initContent, messageFormatter);

    state = setEditorStateAfterInsert(state, content, initContent);

    return state;
};

const appendMentionEntities = (content: ContentState, messageFormatter: MessageFormatter) => {
    content.getBlocksAsArray().forEach(block => {
        const blockText = block.getText();
        const mentions = messageFormatter.findMentionSelections(blockText);

        content = appendMentions(content, mentions, block);
    });

    return content;
};
